import { useState, useEffect, useRef } from "react";
import { FONT, C, CheckIcon, ChevronDown, PlayIcon } from "../lib/programConstants";
import {
  completeSession,
  startSession,
} from "../services/WorkoutEndUserService";
import SetLoggingExerciseCard from "./SetLoggingExerciseCard";
import { toast } from "react-toastify";

// ─── Group Type Color Mapping ────────────────────────────────────────────────

const GROUP_COLORS = {
  warm_up:    { accent: "#7c3aed", bg: "#f5f0ff", badge: "#ede5ff", badgeText: "#6d28d9", dot: "#7c3aed" },
  superset:   { accent: "#2563eb", bg: "#eff6ff", badge: "#dbeafe", badgeText: "#1d4ed8", dot: "#2563eb" },
  circuit:    { accent: "#0d9488", bg: "#f0fdfa", badge: "#ccfbf1", badgeText: "#0f766e", dot: "#0d9488" },
  giant_set:  { accent: "#ea580c", bg: "#fff7ed", badge: "#ffedd5", badgeText: "#c2410c", dot: "#ea580c" },
  amrap:      { accent: "#dc2626", bg: "#fef2f2", badge: "#fee2e2", badgeText: "#b91c1c", dot: "#dc2626" },
  emom:       { accent: "#d97706", bg: "#fffbeb", badge: "#fef3c7", badgeText: "#b45309", dot: "#d97706" },
  cool_down:  { accent: "#1e3a5f", bg: "#f0f4f8", badge: "#e2e8f0", badgeText: "#1e3a5f", dot: "#1e3a5f" },
};

const getGroupColors = (groupType) => {
  return GROUP_COLORS[groupType] || GROUP_COLORS.superset;
};

const getGroupLabel = (groupType) => {
  const labels = {
    warm_up: "WARM-UP",
    superset: "SUPERSET",
    circuit: "CIRCUIT",
    giant_set: "GIANT SET",
    amrap: "AMRAP",
    emom: "EMOM",
    cool_down: "COOL-DOWN",
  };
  return labels[groupType] || groupType?.toUpperCase() || "GROUP";
};

// ─── Video Modal (shared for group cards) ───────────────────────────────────

function VideoModal({ videoUrl, onClose }) {
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?#]+)/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(videoUrl);
  if (!videoId) return null;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.8)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "100%", maxWidth: 480, background: "#000", borderRadius: 12, overflow: "hidden",
      }}>
        <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title="Exercise Demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px" }}>
          <a href={videoUrl} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, color: "#aaa", textDecoration: "underline", fontFamily: FONT }}>
            Open in YouTube
          </a>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "#fff", fontSize: 13,
            fontWeight: 600, cursor: "pointer", fontFamily: FONT, padding: "4px 8px",
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Superset / Circuit Group Card ──────────────────────────────────────────

function GroupRestTimer({ restSeconds }) {
  const [running, setRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(restSeconds);
  const intervalRef = useRef(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (running && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) { clearInterval(intervalRef.current); setRunning(false); setFinished(true); return 0; }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const reset = () => { clearInterval(intervalRef.current); setRunning(false); setFinished(false); setTimeLeft(restSeconds); };
  const toggle = () => { if (finished) { reset(); return; } setRunning((r) => !r); };
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
      borderTop: `1px solid ${C.borderLight}`,
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: FONT, color: C.text, minWidth: 52 }}>
        {mins}:{String(secs).padStart(2, "0")}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: FONT }}>
          {finished ? "Rest Complete" : "Rest between rounds"}
        </div>
        <div style={{ fontSize: 12, color: C.muted, fontFamily: FONT }}>{restSeconds}s programmed</div>
      </div>
      <button onClick={toggle} style={{
        padding: "10px 22px", borderRadius: 20, border: "none", cursor: "pointer",
        fontFamily: FONT, fontSize: 14, fontWeight: 700,
        background: finished ? "#e8f5e9" : running ? C.chip : "#1a3a3a", color: finished ? C.done : running ? C.text : "#fff",
      }}>{finished ? "Reset" : running ? "Pause" : "Start"}</button>
    </div>
  );
}

function SupersetGroupCard({ group, onBlockCompleted, disabled }) {
  const [activeRound, setActiveRound] = useState(1);
  const isAmrap = group.groupType === "amrap";
  const fixedRounds = group.roundNo || 0;
  const [dynamicRounds, setDynamicRounds] = useState(isAmrap ? 1 : fixedRounds);
  const rounds = isAmrap ? dynamicRounds : fixedRounds;
  const children = group.children || [];
  const colors = getGroupColors(group.groupType);
  const groupTypeLabel = getGroupLabel(group.groupType);
  const labels = children.map((c) => c.groupLabel).filter(Boolean);
  const alternateText = labels.length > 0 ? `alternate ${labels.join(" / ")}` : "";

  return (
    <div style={{
      background: C.bg, borderRadius: 14, margin: "10px 12px",
      border: `1px solid ${C.borderLight}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      overflow: "hidden",
    }}>
      {/* Group Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", background: colors.bg, borderBottom: `1px solid ${C.borderLight}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: 4, background: colors.dot }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: C.text, fontFamily: FONT, textTransform: "uppercase", letterSpacing: "0.03em" }}>
            {groupTypeLabel}{isAmrap ? "" : ` · ${rounds} ${rounds === 1 ? "ROUND" : "ROUNDS"}`}
          </span>
        </div>
        {alternateText && (
          <span style={{ fontSize: 12, color: C.muted, fontFamily: FONT }}>{alternateText}</span>
        )}
      </div>

      {/* Children exercises — full interactive cards */}
      <div style={{ margin: "0 -1px" }}>
        {children.map((child) => (
          <SetLoggingExerciseCard
            key={child.id || child.blockId}
            exercise={child}
            onBlockCompleted={onBlockCompleted}
            disabled={disabled}
            groupLabel={child.groupLabel}
            accentColor={colors.accent}
            activeRound={activeRound}
            totalRounds={rounds}
          />
        ))}
      </div>

      {/* Rounds selector */}
      {(rounds > 1 || isAmrap) && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
          borderTop: `1px solid ${C.borderLight}`,
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: FONT }}>
            ROUNDS
          </span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {Array.from({ length: rounds }, (_, i) => i + 1).map((r) => (
              <button key={r} onClick={() => setActiveRound(r)} style={{
                width: 34, height: 34, borderRadius: 17, border: `1.5px solid ${r === activeRound ? colors.accent : C.borderLight}`,
                background: r === activeRound ? colors.accent : "transparent",
                color: r === activeRound ? "#fff" : C.text,
                fontSize: 14, fontWeight: 600, fontFamily: FONT, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {r}
              </button>
            ))}
            {isAmrap && !disabled && (
              <button onClick={() => { const next = dynamicRounds + 1; setDynamicRounds(next); setActiveRound(next); }} style={{
                width: 34, height: 34, borderRadius: 17, border: `1.5px dashed ${C.border}`,
                background: "transparent", color: C.sub,
                fontSize: 18, fontWeight: 400, fontFamily: FONT, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                +
              </button>
            )}
          </div>
        </div>
      )}

      {/* Rest between rounds */}
      {group.restSeconds > 0 && <GroupRestTimer restSeconds={group.restSeconds} />}
    </div>
  );
}

export default function WorkoutStepAuth({ workoutData = null }) {
  const [workout, setWorkout] = useState(workoutData);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [setsCompleted, setSetsCompleted] = useState({});
  const [startingSession, setStartingSession] = useState(false);
  const sessionStarted = useRef(false);
  const autoCompleteTriggered = useRef(false);

  // Preview mode: session is "scheduled" and user hasn't tapped Start yet
  const isPreview = workout?.sessionStatus === "scheduled" && !sessionStarted.current;

  useEffect(() => {
    if (workoutData) {
      setWorkout(workoutData);
      if (workoutData.sessionStatus === "completed") {
        setSaved(true);
        autoCompleteTriggered.current = true;
      }
      // If session is already ongoing, mark as started (no API call needed)
      if (workoutData.sessionStatus === "ongoing") {
        sessionStarted.current = true;
      }
    }
  }, [workoutData]);

  // Explicit start — only when user taps the Start button
  const handleStartSession = async () => {
    if (!workout?.sessionId || startingSession) return;
    setStartingSession(true);
    try {
      await startSession(workout.sessionId);
      sessionStarted.current = true;
      setWorkout((prev) => ({ ...prev, sessionStatus: "ongoing" }));
    } catch {
      toast.error("Workout could not be started. Please try again.");
    }
    finally { setStartingSession(false); }
  };

  if (!workout) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: FONT }}>
        <p style={{ fontSize: 14, color: C.muted }}>No workout data available</p>
      </div>
    );
  }

  // Handle block completion/status change from SetLoggingExerciseCard
  const handleBlockCompleted = (blockId, newStatus, completedCount) => {
    const status = newStatus || "completed";

    // Track per-exercise completed set count for progress bar
    if (completedCount != null) {
      setSetsCompleted((prev) => ({ ...prev, [blockId]: completedCount }));
    }

    setWorkout((prev) => {
      // Don't trigger auto-complete if session is already completed
      if (prev.sessionStatus === "completed") {
        // Still update the exercise status for UI
        return {
          ...prev,
          exercises: prev.exercises.map((item) => {
            if (item._isGroup) {
              return { ...item, children: item.children.map((c) => (c.blockId === blockId || c.instanceId === blockId) ? { ...c, status } : c) };
            }
            return (item.blockId === blockId || item.instanceId === blockId) ? { ...item, status } : item;
          }),
        };
      }

      const updated = {
        ...prev,
        exercises: prev.exercises.map((item) => {
          if (item._isGroup) {
            return {
              ...item,
              children: item.children.map((c) =>
                (c.blockId === blockId || c.instanceId === blockId) ? { ...c, status } : c
              ),
            };
          }
          return (item.blockId === blockId || item.instanceId === blockId) ? { ...item, status } : item;
        }),
      };

      // Check if all exercises are now completed → auto-complete session
      if (status === "completed") {
        const allEx = [];
        (updated.exercises || []).forEach((item) => {
          if (item._isGroup) { item.children.forEach((c) => allEx.push(c)); }
          else { allEx.push(item); }
        });
        const allDone = allEx.length > 0 && allEx.every((ex) => ex.status === "completed");
        if (allDone && !autoCompleteTriggered.current) {
          autoCompleteTriggered.current = true;
          setTimeout(() => autoCompleteSession(), 0);
        }
      }

      return updated;
    });
  };

  // Auto-complete session when all exercises are done
  const autoCompleteSession = async () => {
    if (!workout.sessionId || saved || saving) return;
    setSaving(true);
    try {
      await completeSession(workout.sessionId, {});
      setWorkout((prev) => ({ ...prev, sessionStatus: "completed" }));
      setSaved(true);
      toast.success("🎉 All exercises done — workout saved!");
    } catch { /* silent — user can still tap the button manually */ }
    finally { setSaving(false); }
  };

  // Mark All Complete: single API call — backend handles everything
  const markAllComplete = async () => {
    if (!workout.sessionId || saving) return;
    setSaving(true);

    try {
      await completeSession(workout.sessionId, {});

      setWorkout((prev) => ({
        ...prev,
        sessionStatus: "completed",
        exercises: prev.exercises.map((item) => {
          if (item._isGroup) {
            return { ...item, children: item.children.map((c) => ({ ...c, status: "completed" })) };
          }
          return { ...item, status: "completed" };
        }),
      }));
      setSaved(true);
      toast.success("✓ Workout complete!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to complete workout");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWorkout = async () => {
    if (!workout.sessionId || saved || saving) return;
    await markAllComplete();
  };

  const getAllExercises = (items) => {
    const flat = [];
    (items || []).forEach((item) => {
      if (item._isGroup) {
        item.children.forEach((c) => flat.push(c));
      } else {
        flat.push(item);
      }
    });
    return flat;
  };

  const allExercises = getAllExercises(workout.exercises);
  const completedExercises = allExercises.filter(exercise => exercise.status === 'completed');
  // For progress: count total sets across all exercises (accounting for rounds)
  const getExerciseTotalSets = (e) => {
    if (e.roundNo && e.roundNo > 0) {
      return e.roundNo * (parseInt(e.sets) || 1);
    }
    return parseInt(e.sets) || 1;
  };
  const totalSets = allExercises.reduce((a, e) => a + getExerciseTotalSets(e), 0);
  // Sum completed sets from per-exercise tracking (real-time, not status-dependent)
  const doneSets = Object.values(setsCompleted).reduce((a, count) => a + count, 0);
  const allExDone = workout.sessionStatus === "completed" || (allExercises.length > 0 &&
    (completedExercises.length === allExercises.length || doneSets >= totalSets));
  const progress = workout.sessionStatus === "completed"
    ? 100
    : totalSets > 0
      ? Math.min((doneSets / totalSets) * 100, 100)
      : 0;
  const displayDoneSets = workout.sessionStatus === "completed" ? totalSets : doneSets;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT, maxWidth: 480, margin: "0 auto" }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes dotBounce { from { opacity: 0.3; transform: translateY(0); } to { opacity: 1; transform: translateY(-3px); } }
        * { box-sizing:border-box; margin:0; padding:0; }
      `}</style>

      {/* Header */}
      <div style={{ padding: "48px 16px 16px" }}>
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 4 }}>
          {workout.coachName} · {workout.date}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.text, lineHeight: 1.2, marginBottom: 2 }}>{workout.dayLabel}</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>{workout.programName} · Week {workout.weekNumber}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, height: 3, background: C.borderLight, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: allExDone ? C.done : C.text, borderRadius: 2, transition: "width 0.3s ease" }} />
          </div>
          {totalSets > 0 ? (
            <div style={{ fontSize: 13, fontWeight: 600, color: allExDone ? C.done : C.sub, fontFamily: FONT }}>{displayDoneSets}/{totalSets} sets</div>
          ) : (
            <div style={{ fontSize: 13, fontWeight: 600, color: allExDone ? C.done : C.sub, fontFamily: FONT }}>{completedExercises.length}/{allExercises.length}</div>
          )}
        </div>
      </div>

      {/* Preview Mode Banner — show when session is scheduled and not yet started */}
      {isPreview && allExercises.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px", margin: "0 12px 16px", background: C.bg,
          borderRadius: 12, border: `1px solid ${C.borderLight}`, boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>👁</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: FONT }}>Preview Mode</div>
              <div style={{ fontSize: 12, color: C.muted, fontFamily: FONT }}>Tap start when you're ready to log</div>
            </div>
          </div>
          <button onClick={handleStartSession} disabled={startingSession} style={{
            display: "flex", alignItems: "center", gap: 5, padding: "8px 18px",
            borderRadius: 20, background: C.dark, border: "none", cursor: startingSession ? "not-allowed" : "pointer",
            opacity: startingSession ? 0.7 : 1, transition: "opacity 0.15s",
          }}>
            {startingSession ? (
              <span style={{ display: "flex", gap: 2, alignItems: "center" }}>
                {[0, 1, 2].map((i) => (
                  <span key={i} style={{ width: 4, height: 4, borderRadius: 2, background: "#fff", animation: `dotBounce 0.6s ${i * 0.15}s infinite alternate` }} />
                ))}
              </span>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 1L10 6L2.5 11V1Z" fill="#fff"/></svg>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", fontFamily: FONT }}>Start</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Exercise cards with set logging */}
      {allExercises.length === 0 && !saved && (
        <p role="status" style={{ padding: "20px 16px", color: C.sub, fontSize: 14 }}>
          No exercises have been added to this workout yet. Contact your coach before starting.
        </p>
      )}
      {workout.exercises.map((item, idx) => {
        if (item._isGroup) {
          return (
            <SupersetGroupCard
              key={`group-${idx}`}
              group={item}
              onBlockCompleted={handleBlockCompleted}
              disabled={saved || isPreview}
            />
          );
        }
        return (
          <SetLoggingExerciseCard
            key={item.id}
            exercise={item}
            onBlockCompleted={handleBlockCompleted}
            disabled={saved || isPreview}
          />
        );
      })}

      {/* Mark All Complete button */}
      {allExercises.length > 0 && !allExDone && !saved && !isPreview && (
        <div style={{ padding: "20px 16px 8px" }}>
          <button onClick={markAllComplete} disabled={saving} style={{
            width: "100%", padding: "12px", borderRadius: 8, border: `1px solid ${C.border}`,
            background: C.bg, color: C.sub, fontSize: 14, fontWeight: 600, fontFamily: FONT, cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1,
          }}>{saving ? "Completing..." : "Mark All Complete"}</button>
        </div>
      )}

      {/* Workout saved badge (before all done) */}
      {saved && !allExDone && (
        <div style={{ padding: "20px 16px", textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "12px 20px",
            borderRadius: 8, background: "#f0faf0", border: "1px solid #c8e6c9", color: C.done, fontSize: 14, fontWeight: 600, fontFamily: FONT,
          }}><CheckIcon /> Workout Saved</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Your progress has been saved</div>
        </div>
      )}

      {/* All done state */}
      {allExDone && (
        <div style={{ textAlign: "center", padding: "36px 16px", animation: "fadeIn 0.3s ease" }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.done, marginBottom: 4 }}>Workout Complete</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
            {saved ? "Your workout has been saved and your coach has been notified" : "Review your exercises above, then save your workout."}
          </div>
          {!saved ? (
            <button onClick={handleSaveWorkout} disabled={saving} style={{
              width: "100%", maxWidth: 260, padding: "12px 24px", borderRadius: 8, border: "none",
              background: saving ? C.border : C.dark, color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: FONT,
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8, margin: "0 auto",
            }}>
              {saving ? (
                <>
                  <div style={{ width: 16, height: 16, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />
                  Saving Workout...
                </>
              ) : "Save Workout"}
            </button>
          ) : (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "12px 24px",
              borderRadius: 8, background: "#f0faf0", border: "1px solid #c8e6c9", color: C.done, fontSize: 15, fontWeight: 600, fontFamily: FONT,
            }}><CheckIcon /> Workout Saved</div>
          )}
        </div>
      )}

      <div style={{ height: 48 }} />
    </div>
  );
}
